import { getPublicKey, generateSecretKey } from 'nostr-tools';
import { nip19 } from 'nostr-tools';

/**
 * NIP-41 Configuration Module
 * 
 * All protocol constants in one place for easy retargeting when the draft changes.
 * Target: pf7z-nip41 branch of nostr-protocol/nips (PR #829)
 */

// Event kinds
export const NIP41_KIND_WHITELIST = 1776;
export const NIP41_KIND_MIGRATION = 1777;
export const NIP41_KIND_TIMESTAMP_PROOF = 1040; // NIP-03 attestation

// Tag names
export const NIP41_TAG_SUCCESSOR = 'p';
export const NIP41_TAG_WHITELIST_EVENT = 'e';
export const NIP41_TAG_PROOF = 'proof';
export const NIP41_TAG_RELAYS = 'relays';
export const NIP41_TAG_ALT = 'alt';

// Contestation window in days
// The draft currently specifies 60 days (was 30 in earlier commits)
export const NIP41_CONTESTATION_WINDOW_DAYS = 60;
export const NIP41_CONTESTATION_WINDOW_MS = NIP41_CONTESTATION_WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Draft version info displayed on signing screens
export const NIP41_DRAFT_VERSION = 'pf7z-nip41 (PR #829)';
export const NIP41_DRAFT_COMMIT = 'draft-optional';
export const NIP41_SPEC_WARNING = `This app targets ${NIP41_DRAFT_VERSION}. NIP-41 is an unmerged draft — events published today may not be compatible with future revisions.`;

// Whitelist content policy
export const NIP41_WHITELIST_CONTENT_SHOULD_BE_EMPTY = true;

// Local storage keys
export const LS_FIRST_SEEN_PREFIX = 'heir:first-seen:';
export const LS_PENDING_PROOFS = 'heir:pending-proofs';
export const LS_DRAFT_SUCCESSOR = 'heir:draft-successor';
export const LS_RECENT_INSPECTED = 'heir:recent-inspected';

/**
 * Generate a new successor keypair
 */
export function generateSuccessorKey(): { nsec: string; npub: string; pubkey: string } {
  const sk = generateSecretKey();
  const pubkey = getPublicKey(sk);
  const nsec = nip19.nsecEncode(sk);
  const npub = nip19.npubEncode(pubkey);
  
  return { nsec, npub, pubkey };
}

/**
 * Validate an npub string
 */
export function validateNpub(npub: string): { valid: boolean; pubkey?: string; error?: string } {
  try {
    const decoded = nip19.decode(npub);
    if (decoded.type === 'npub') {
      return { valid: true, pubkey: decoded.data };
    }
    return { valid: false, error: 'Not an npub' };
  } catch {
    return { valid: false, error: 'Invalid npub format' };
  }
}

/**
 * Build whitelist event tags
 */
export function buildWhitelistTags(successorPubkey: string): string[][] {
  const tags: string[][] = [
    [NIP41_TAG_SUCCESSOR, successorPubkey],
    [NIP41_TAG_ALT, 'NIP-41 successor whitelist'],
  ];
  return tags;
}

/**
 * Build migration event tags
 */
export function buildMigrationTags(
  oldPubkey: string,
  whitelistEventId: string,
  proofEventId: string,
  relayHints?: string[]
): string[][] {
  const tags: string[][] = [
    [NIP41_TAG_SUCCESSOR, oldPubkey],
    [NIP41_TAG_WHITELIST_EVENT, whitelistEventId],
    [NIP41_TAG_PROOF, proofEventId],
    [NIP41_TAG_ALT, 'NIP-41 migration declaration'],
  ];
  
  if (relayHints && relayHints.length > 0) {
    tags.push([NIP41_TAG_RELAYS, ...relayHints]);
  }
  
  return tags;
}

/**
 * Build timestamp proof event tags (NIP-03)
 */
export function buildTimestampProofTags(targetEventId: string): string[][] {
  return [
    ['e', targetEventId],
    [NIP41_TAG_ALT, 'OpenTimestamps attestation'],
  ];
}

/**
 * Check if a whitelist event is valid
 */
export function isValidWhitelistEvent(event: any): boolean {
  if (!event || event.kind !== NIP41_KIND_WHITELIST) return false;
  const pTags = event.tags?.filter((t: string[]) => t[0] === NIP41_TAG_SUCCESSOR);
  return pTags?.length === 1 && !!pTags[0][1];
}

/**
 * Check if a migration event is valid
 */
export function isValidMigrationEvent(event: any): boolean {
  if (!event || event.kind !== NIP41_KIND_MIGRATION) return false;
  const pTag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_SUCCESSOR);
  const eTag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_WHITELIST_EVENT);
  const proofTag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_PROOF);
  return !!pTag?.[1] && !!eTag?.[1] && !!proofTag?.[1];
}

/**
 * Extract successor pubkey from whitelist event
 */
export function getWhitelistSuccessor(event: any): string | undefined {
  const tag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_SUCCESSOR);
  return tag?.[1];
}

/**
 * Extract old pubkey from migration event
 */
export function getMigrationOldPubkey(event: any): string | undefined {
  const tag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_SUCCESSOR);
  return tag?.[1];
}

/**
 * Extract referenced whitelist event id from migration event
 */
export function getMigrationWhitelistRef(event: any): string | undefined {
  const tag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_WHITELIST_EVENT);
  return tag?.[1];
}

/**
 * Extract proof event id from migration event
 */
export function getMigrationProofRef(event: any): string | undefined {
  const tag = event.tags?.find((t: string[]) => t[0] === NIP41_TAG_PROOF);
  return tag?.[1];
}

// Types
export interface Nip41ChainState {
  pubkey: string;
  npub: string;
  state: 'unarmed' | 'armed' | 'migrating' | 'settled' | 'contested';
  whitelistEvent?: any;
  whitelistProof?: any;
  migrationEvent?: any;
  migrationProof?: any;
  predecessor?: Nip41ChainState;
  successor?: Nip41ChainState;
  firstSeenAt?: number;
  contestationEndAt?: number;
}

export interface PendingProof {
  targetEventId: string;
  targetKind: number;
  otsProofBase64: string;
  createdAt: number;
  upgraded: boolean;
}
